/**
 * spriteAnim — pure procedural locomotion + action transforms.
 *
 * No Phaser import. Drives off AnimState + frame counters. Returns ADDITIVE /
 * MULTIPLICATIVE offsets to compose with elevation + facing flip.
 */

import type { AnimState } from '@/systems/AnimationStateMachine';

export interface LocomotionTransform {
  /** Additive vertical offset in pixels (positive = down). */
  dy: number;
  /** Multiplicative scale override for X (1 = no change). */
  scaleX: number;
  /** Multiplicative scale override for Y (1 = no change). */
  scaleY: number;
  /** Additive rotation in degrees (positive = clockwise). */
  angleDeg: number;
}

/** Visual transform driven by action states (attack, hurt, block, jump, …). */
export interface ActionTransform {
  /** Additive horizontal visual nudge (positive = right). */
  dx: number;
  /** Additive vertical visual offset. */
  dy: number;
  /** Multiplicative scale override for X (1 = no change). */
  scaleX: number;
  /** Multiplicative scale override for Y (1 = no change). */
  scaleY: number;
  /** Additive rotation in degrees. */
  angleDeg: number;
}

const IDENTITY: LocomotionTransform = { dy: 0, scaleX: 1, scaleY: 1, angleDeg: 0 };
const ACTION_IDENTITY: ActionTransform = { dx: 0, dy: 0, scaleX: 1, scaleY: 1, angleDeg: 0 };

/**
 * Compute the procedural locomotion transform for a given state + frame time.
 *
 * @param state  Current AnimState from the AnimationStateMachine.
 * @param tFrames  Monotonic frame counter (increments each game update, ~60 Hz).
 * @param facing   Entity facing direction (1 = right, -1 = left). Used for lean.
 */
export function locomotionTransform(
  state: AnimState,
  tFrames: number,
  facing: 1 | -1 = 1,
): LocomotionTransform {
  switch (state) {
    case 'idle': {
      // Slow gentle bob (period ~2 s at 60 fps = 120 frames) + subtle breathing scaleY
      const t = (tFrames / 120) * Math.PI * 2;
      const dy = Math.sin(t) * 2.5; // ±2.5 px
      const scaleY = 1 + Math.sin(t) * 0.03; // ±3%
      return { dy, scaleX: 1, scaleY, angleDeg: 0 };
    }

    case 'walk': {
      // Faster + larger bob (period ~0.6 s = 36 frames) + 3° forward lean + step squash
      const t = (tFrames / 36) * Math.PI * 2;
      const dy = Math.sin(t) * 4; // ±4 px
      const scaleY = 1 - Math.abs(Math.sin(t)) * 0.04; // squash at extremes
      const angleDeg = facing * 3; // lean toward facing
      return { dy, scaleX: 1, scaleY, angleDeg };
    }

    case 'run': {
      // Faster (period ~0.4 s = 24 frames) + larger amplitude + stronger lean
      const t = (tFrames / 24) * Math.PI * 2;
      const dy = Math.sin(t) * 6; // ±6 px
      const scaleY = 1 - Math.abs(Math.sin(t)) * 0.06;
      const angleDeg = facing * 5;
      return { dy, scaleX: 1, scaleY, angleDeg };
    }

    default:
      // All other states (attack, hurt, ko, jump, …) — identity; action transform handles those.
      return IDENTITY;
  }
}

/**
 * Compute the procedural ACTION transform for a given state + frames-in-state.
 *
 * Returns an additive/multiplicative offset that overrides locomotion while an
 * action state is active. For locomotion states (idle/walk/run) returns identity
 * so it composes cleanly: multiply scales, add offsets.
 *
 * @param state    Current AnimState.
 * @param tFrames  Frames elapsed since entering this state (0 on entry frame).
 * @param facing   Entity facing direction (1 = right, -1 = left).
 */
export function actionTransform(
  state: AnimState,
  tFrames: number,
  facing: 1 | -1 = 1,
): ActionTransform {
  switch (state) {
    case 'attackL': {
      // anticipation (0-2) → lunge (3-8) → recover (9+)
      if (tFrames <= 2) {
        const p = tFrames / 3;
        return { dx: -facing * 4 * p, dy: 0, scaleX: 0.95, scaleY: 1.05, angleDeg: 0 };
      }
      if (tFrames <= 8) {
        const p = (tFrames - 2) / 6;
        return {
          dx: facing * 12 * p,
          dy: 0,
          scaleX: 1 + 0.12 * p,
          scaleY: 1 - 0.12 * p,
          angleDeg: 0,
        };
      }
      // recover back to identity over 5 frames
      const rp = Math.min((tFrames - 8) / 5, 1);
      return {
        dx: facing * 12 * (1 - rp),
        dy: 0,
        scaleX: 1 + 0.12 * (1 - rp),
        scaleY: 1 - 0.12 * (1 - rp),
        angleDeg: 0,
      };
    }

    case 'attackH': {
      // bigger/slower — anticipation (0-4) → lunge (5-13) → recover (14+)
      if (tFrames <= 4) {
        const p = tFrames / 5;
        return { dx: -facing * 6 * p, dy: 0, scaleX: 0.9, scaleY: 1.08, angleDeg: 0 };
      }
      if (tFrames <= 13) {
        const p = (tFrames - 4) / 9;
        return {
          dx: facing * 18 * p,
          dy: 0,
          scaleX: 1 + 0.18 * p,
          scaleY: 1 - 0.18 * p,
          angleDeg: 0,
        };
      }
      const rp = Math.min((tFrames - 13) / 7, 1);
      return {
        dx: facing * 18 * (1 - rp),
        dy: 0,
        scaleX: 1 + 0.18 * (1 - rp),
        scaleY: 1 - 0.18 * (1 - rp),
        angleDeg: 0,
      };
    }

    case 'hurt': {
      // Recoil: push back away from facing, lean, decaying jitter — decays over 10 frames
      const decay = Math.max(1 - tFrames / 10, 0);
      const jitter = Math.sin(tFrames * 3.1) * 1.5 * decay;
      return {
        dx: -facing * 8 * decay,
        dy: jitter,
        scaleX: 1,
        scaleY: 1 - 0.06 * decay,
        angleDeg: -facing * 10 * decay,
      };
    }

    case 'block':
      // Static brace: lean back + squash Y — held for the duration of block
      return { dx: -facing * 4, dy: 0, scaleX: 1, scaleY: 0.92, angleDeg: -facing * 5 };

    case 'jump': {
      // Stretch upward (ascending), ease to identity after ~20 frames
      if (tFrames < 20) {
        const ease = 1 - tFrames / 20;
        return { dx: 0, dy: 0, scaleX: 1 - 0.05 * ease, scaleY: 1 + 0.1 * ease, angleDeg: 0 };
      }
      return ACTION_IDENTITY;
    }

    case 'throw':
    case 'special': {
      // Forward flourish lunge: peak at frame 6, recover over 5 frames
      if (tFrames <= 6) {
        const p = tFrames / 6;
        return {
          dx: facing * 10 * p,
          dy: 0,
          scaleX: 1 + 0.08 * p,
          scaleY: 1 - 0.08 * p,
          angleDeg: 0,
        };
      }
      const rp = Math.min((tFrames - 6) / 5, 1);
      return {
        dx: facing * 10 * (1 - rp),
        dy: 0,
        scaleX: 1 + 0.08 * (1 - rp),
        scaleY: 1 - 0.08 * (1 - rp),
        angleDeg: 0,
      };
    }

    default:
      // ko: identity — setAngle(90)+fade handled by Entity.knockOut
      // idle/walk/run/taunt: identity — locomotionTransform covers those
      return ACTION_IDENTITY;
  }
}
